import unescapedText from 'I18n/utils/unescapedText';
import { View } from 'foundations-components/components/View';
import { CardGrid } from 'foundations-components/transitional/components/CardGrid';
import { ErrorMessage } from 'foundations-components/transitional/components/ErrorMessage';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { CollectionCard as DSCollectionCard } from 'marketplace-discovery-studio-lib/components/CollectionCard';
import { useResponsiveProps } from 'marketplace-ui-common/hooks/useResponsiveProps';
import { UsageScreenProvider, useTrackMarketplaceEvent } from 'marketplace-ui-tracking';
import { getPageViewedEvent, getSearchResultClickedEvent, SearchResultViewTracker } from 'marketplace-ui-tracking/events/marketplace';
import { useCallback, useEffect } from 'react';
import { NavMarker } from 'react-rhumb';
import SanitizedText from 'sanitize-text/components/SanitizedText';
import { UmbrellaCollectionCard } from '../collections/components/UmbrellaCollectionCard';
import { adaptDSPreviewItems, getCollectionUrl, useDiscoveryCollections } from '../collections/hooks/useDiscoveryCollections';
import { StorefrontDashboardWithTabs } from '../components/StorefrontDashboardWithTabs';
import { StorefrontPageMetadata } from '../components/StorefrontPageMetadata';
import { Screens } from '../constants/usage';
import { ScrollToTopOnMount } from '../hooks/useScrollToTop';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
export function FeaturedPage() {
  return /*#__PURE__*/_jsxs(UsageScreenProvider, {
    screen: Screens.FEATURED,
    children: [/*#__PURE__*/_jsx(ScrollToTopOnMount, {}), /*#__PURE__*/_jsx(FeaturedPageContent, {})]
  });
}
export function FeaturedPageContent() {
  const trackMarketplaceEvent = useTrackMarketplaceEvent();
  const {
    isMobile
  } = useResponsiveProps({
    isMobile: {
      xs: true,
      lg: false
    }
  });
  const {
    collections,
    loading,
    error
  } = useDiscoveryCollections();
  const trackCollectionClicked = useCallback((id, slug, index, resultCount) => {
    trackMarketplaceEvent(getSearchResultClickedEvent({
      listingId: id,
      resultCount,
      resultIndex: index,
      resultType: 'COLLECTION',
      slug
    }));
  }, [trackMarketplaceEvent]);
  useEffect(() => {
    trackMarketplaceEvent(getPageViewedEvent({}));
  }, [trackMarketplaceEvent]);
  const renderCollectionCard = (collection, index, resultCount) => /*#__PURE__*/_jsx(SearchResultViewTracker, {
    category: collection.slug,
    collectionId: collection.entityId,
    listingId: collection.entityId,
    resultCount: resultCount,
    resultIndex: index,
    resultType: "COLLECTION",
    slug: collection.slug,
    children: /*#__PURE__*/_jsx(DSCollectionCard, {
      title: collection.name,
      description: collection.description ? /*#__PURE__*/_jsx(SanitizedText, {
        body: collection.description,
        WrapperComponent: "span"
      }) : undefined,
      buttonText: unescapedText('marketplaceStorefront.collectionCard.viewCollection'),
      buttonUrl: getCollectionUrl(collection.slug),
      previewItems: adaptDSPreviewItems(collection.previewItems),
      listingCount: collection.listingCount,
      onClick: () => trackCollectionClicked(collection.entityId, collection.slug, index, resultCount)
    })
  }, collection.slug);
  const content = loading ? /*#__PURE__*/_jsx(LoadingSpinner, {
    grow: true,
    size: "medium",
    marginBlockStart: "800"
  }) : error ? /*#__PURE__*/_jsx(ErrorMessage, {
    type: "badRequest"
  }) : /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(CardGrid, {
      columns: isMobile ? 1 : 2,
      gap: "400",
      children: collections.slice(0, 2).map((collection, index) => renderCollectionCard(collection, index, collections.length))
    }), /*#__PURE__*/_jsx(View, {
      marginBlock: "400",
      children: /*#__PURE__*/_jsx(UmbrellaCollectionCard, {
        title: unescapedText('marketplaceStorefront.umbrellaCollections.aiEssentials.title'),
        description: unescapedText('marketplaceStorefront.umbrellaCollections.aiEssentials.description'),
        slug: "ai-essentials"
      })
    }), /*#__PURE__*/_jsx(CardGrid, {
      columns: isMobile ? 1 : 2,
      gap: "400",
      children: collections.slice(2).map((collection, index) => renderCollectionCard(collection, index + 2, collections.length))
    })]
  });
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [collections.length > 0 && /*#__PURE__*/_jsx(NavMarker, {
      name: "FEATURED_LOAD"
    }), /*#__PURE__*/_jsx(StorefrontPageMetadata, {
      loading: loading,
      title: unescapedText('marketplaceStorefront.documentHead.featured.title'),
      metaDescription: unescapedText('marketplaceStorefront.documentHead.featured.metaDescription')
    }), /*#__PURE__*/_jsx(StorefrontDashboardWithTabs, {
      children: content
    })]
  });
}